// İSG uzmanı portalı — KKD (Kişisel Koruyucu Donanım) kataloğu (Ayarlar; api-contract §24,
// tenant-scoped). Firma kendi KKD kataloğunu yönetir: genel (bizim) varsayılan liste ("Genel" rozeti)
// + firma özel öge. Liste (UTable sözleşmesi: page/pageSize/search + total/filteredTotal) + aktif
// filtresi, oluştur/düzenle (ProtectiveEquipmentFormModal), **pasifleştir/sil** (soft, onay). Genel
// öge de düzenlenebilir/pasifleştirilebilir (`isDefault` backend'de korunur — §24.1). KKD ögeleri
// permit-tipine (§24.2) bağlanır; izin detayında read-only görünür. Auth gate = `permit-types` (D3).

import {
  Alert,
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  EmptyState,
  Pagination,
  type ProtectiveEquipmentDto,
  RowActionsMenu,
  SelectMenu,
  type SelectOption,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  TableSkeletonRows,
  TableToolbar,
  TableToolbarFilter,
  Tag,
  protectiveEquipmentApi,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HardHat, Plus, SearchX } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShellHeaderAction } from '../../components/layout/ShellHeaderAction';
import { ProtectiveEquipmentFormModal } from '../../components/permit-types/ProtectiveEquipmentFormModal';
import { protectiveEquipmentKeys } from '../../lib/protectiveEquipmentKeys';
import { useTableDensity } from '../../lib/uiPrefs';
import { useDebouncedValue } from '../../lib/useDebouncedValue';

const PAGE_SIZE = 20;

/** Aktif filtresi string değeri → `isActive` param (undefined = hepsi). */
function activeParam(value: string): boolean | undefined {
  if (value === 'active') {
    return true;
  }
  if (value === 'inactive') {
    return false;
  }
  return undefined;
}

export function ProtectiveEquipmentListPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [message, setMessage] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState<ProtectiveEquipmentDto | null>(null);
  const [deleteItem, setDeleteItem] = useState<ProtectiveEquipmentDto | null>(null);

  function resetPage<T>(setter: (v: T) => void) {
    return (value: T) => {
      setter(value);
      setPage(1);
    };
  }

  const statusOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: t('permitTypes.list.filter.allStatuses') },
      { value: 'active', label: t('permitTypes.list.filter.active') },
      { value: 'inactive', label: t('permitTypes.list.filter.inactive') },
    ],
    [t],
  );

  const params = {
    isActive: activeParam(statusFilter),
    search: debouncedSearch,
    page,
    pageSize: PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: protectiveEquipmentKeys.listParams(params),
    queryFn: () => protectiveEquipmentApi.listProtectiveEquipment(params),
    placeholderData: (prev) => prev,
  });

  const deleteMutation = useMutation({
    mutationFn: (uuid: string) => protectiveEquipmentApi.deleteProtectiveEquipment(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: protectiveEquipmentKeys.list() });
      queryClient.invalidateQueries({ queryKey: protectiveEquipmentKeys.options() });
      setMessage(t('permitTypes.catalog.ppe.deleted'));
      setDeleteItem(null);
    },
  });

  const rows = query.data?.items ?? [];
  const [density, setDensity] = useTableDensity();
  const hasActiveFilters = Boolean(debouncedSearch || statusFilter);
  function clearFilters() {
    setSearch('');
    setStatusFilter('');
    setPage(1);
  }

  return (
    <div className="flex flex-col gap-5">
      <ShellHeaderAction>
        <Button
          onClick={() => {
            setMessage(null);
            setCreateOpen(true);
          }}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('permitTypes.catalog.ppe.new')}
        </Button>
      </ShellHeaderAction>

      {message ? <Alert tone="success">{message}</Alert> : null}

      <Card className="carbon:p-0">
        <CardContent className="flex flex-col gap-3 carbon:gap-0 carbon:p-0">
          <TableToolbar
            summary={
              query.data
                ? t('permitTypes.catalog.ppe.count', { count: query.data.filteredTotal })
                : undefined
            }
            search={search}
            onSearchChange={resetPage(setSearch)}
            searchLabel={t('permitTypes.catalog.ppe.search')}
            searchPlaceholder={t('permitTypes.catalog.ppe.searchPlaceholder')}
            density={density}
            onDensityChange={setDensity}
            densityLabel={t('common.compactRows')}
            filters={
              <TableToolbarFilter>
                <SelectMenu
                  ariaLabel={t('permitTypes.list.filter.status')}
                  placeholder={t('permitTypes.list.filter.allStatuses')}
                  options={statusOptions}
                  value={statusFilter}
                  onValueChange={resetPage(setStatusFilter)}
                />
              </TableToolbarFilter>
            }
          />

          <Table caption={t('permitTypes.catalog.ppe.title')} density={density}>
            <TableHeader>
              <TableRow>
                <TableHead>{t('permitTypes.catalog.ppe.col.name')}</TableHead>
                <TableHead>{t('permitTypes.catalog.col.origin')}</TableHead>
                <TableHead>{t('permitTypes.list.col.status')}</TableHead>
                <TableHead className="w-12">
                  <span className="sr-only">{t('permitTypes.common.actions')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody aria-busy={query.isPending || undefined}>
              {query.isPending ? (
                <TableSkeletonRows columns={4} />
              ) : query.isError ? (
                <TableMessageRow colSpan={4}>
                  {resolveApiErrorMessage(query.error, t)}
                </TableMessageRow>
              ) : rows.length === 0 ? (
                <TableMessageRow colSpan={4}>
                  {hasActiveFilters ? (
                    <EmptyState
                      icon={SearchX}
                      title={t('permitTypes.catalog.ppe.noResults')}
                      description={t('common.noResults.body')}
                      action={
                        <Button variant="ghost" size="sm" onClick={clearFilters}>
                          {t('common.clearFilters')}
                        </Button>
                      }
                      role="status"
                    />
                  ) : (
                    <EmptyState
                      icon={HardHat}
                      title={t('permitTypes.catalog.ppe.empty')}
                      description={t('permitTypes.catalog.ppe.empty.body')}
                      action={
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setMessage(null);
                            setCreateOpen(true);
                          }}
                        >
                          <Plus className="h-4 w-4" aria-hidden="true" />
                          {t('permitTypes.catalog.ppe.new')}
                        </Button>
                      }
                    />
                  )}
                </TableMessageRow>
              ) : (
                rows.map((item) => (
                  <TableRow key={item.uuid}>
                    <TableCell>
                      {/* Birincil "aç" = ad hücresi (S33 Slice 4 QA fix, defekt #4) — ayrı bir
                          görünüm rotası yok, "açmak" düzenleme modalını açmakla AYNI. */}
                      <button
                        type="button"
                        onClick={() => {
                          setMessage(null);
                          setEditItem(item);
                        }}
                        className="block text-left font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {item.name}
                      </button>
                      {item.description ? (
                        <span className="block text-xs text-muted">{item.description}</span>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Tag color={item.isDefault ? 'blue' : 'gray'}>
                        {item.isDefault
                          ? t('permitTypes.catalog.origin.default')
                          : t('permitTypes.catalog.origin.custom')}
                      </Tag>
                    </TableCell>
                    <TableCell>
                      <Tag color={item.isActive ? 'green' : 'gray'}>
                        {item.isActive
                          ? t('permitTypes.status.active')
                          : t('permitTypes.status.inactive')}
                      </Tag>
                    </TableCell>
                    <TableCell className="w-12 px-2">
                      <RowActionsMenu
                        ariaLabel={t('permitTypes.list.action.openMenu', { name: item.name })}
                        items={[
                          {
                            key: 'delete',
                            label: t('permitTypes.list.action.delete'),
                            destructive: true,
                            onSelect: () => {
                              setMessage(null);
                              setDeleteItem(item);
                            },
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {query.data ? (
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              filteredTotal={query.data.filteredTotal}
              onPageChange={setPage}
              labels={{
                nav: t('permitTypes.pagination.nav'),
                previous: t('permitTypes.pagination.previous'),
                next: t('permitTypes.pagination.next'),
              }}
            />
          ) : null}
        </CardContent>
      </Card>

      <ProtectiveEquipmentFormModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSuccess={(msg) => setMessage(msg)}
      />
      <ProtectiveEquipmentFormModal
        open={editItem !== null}
        onOpenChange={(open) => !open && setEditItem(null)}
        item={editItem}
        onSuccess={(msg) => setMessage(msg)}
      />
      <ConfirmDialog
        open={deleteItem !== null}
        onOpenChange={(open) => !open && setDeleteItem(null)}
        title={t('permitTypes.catalog.ppe.delete.title')}
        body={t('permitTypes.catalog.ppe.delete.body', { name: deleteItem?.name ?? '' })}
        confirmLabel={t('permitTypes.catalog.delete.confirm')}
        loading={deleteMutation.isPending}
        error={deleteMutation.isError ? deleteMutation.error : undefined}
        onConfirm={() => deleteItem && deleteMutation.mutate(deleteItem.uuid)}
      />
    </div>
  );
}
